import { bookActions } from '@/actions';
import { bookingFlowTrackingSession } from '@/helpers/checkoutFlowTrackingSession';
import Snackbar from '@/components/elements/notifications/Snackbar/Snackbar';
import { StatusMessage } from '@/components/elements/StatusMessage';
import { ROUTE_BUILDER, toSlugId } from '@/constants/pages';
import { SMART_LINKS } from '@/constants/smartLinkConstants';
import {
  getNewExtraTimeAddOns,
  getRecommendedServices,
} from '@/components/modules/modals/BookAppointment/BookAppointment.helpers';
import ConfirmChangeBookingTimeModal from '@/components/modules/modals/ConfirmChangeBookingTime/ConfirmChangeBookingTime';
import WaitlistModal from '@/components/modules/modals/WaitlistModal';
import { useBookingFlowChrome } from '@/features/booking-flow/BookingFlowChromeContext';
import BookingFlowPage from '@/features/booking-flow/BookingFlowPage';
import { resolveCalendarSelectionFromUrl } from '@/features/booking-flow/resolveCalendarSelectionFromUrl';
import { selectionPatchFromBookState } from '@/features/booking-flow/selectionPatchFromBookState';
import { addServiceTrackingHandlers, syncTrackedServiceIds, trackAddService } from '@/features/booking-flow/tracking';
import { BookingFlowSelectionPatch, serialiseSelection } from '@/features/booking-flow/urlState';
import SelectedEmployeePanel from '@/features/booking-selection/components/SelectedEmployeePanel';
import SelectEmployeeSheet from '@/features/booking-selection/components/SelectEmployeeSheet';
import SelectionDrawer from '@/features/booking-selection/components/SelectionDrawer';
import type { SelectionDrawerEmployee } from '@/features/booking-selection/components/SelectionDrawer.types';
import SelectionModal from '@/features/booking-selection/components/SelectionModal';
import type { SelectionModalView } from '@/features/booking-selection/components/SelectionModal.types';
import SelectionsBar from '@/features/booking-selection/components/SelectionsBar';
import { useSelectionsBar } from '@/features/booking-selection/components/SelectionsBar.hooks';
import SelectionSummary from '@/features/booking-selection/components/SelectionSummary';
import { ANY_EMPLOYEE_ID, getAnyEmployee } from '@/features/booking-selection/constants';
import {
  buildBookSelectionSummary,
  getOrphanedExtraTimeAddOnIds,
} from '@/features/booking-selection/selectionSummary.helpers';
import { useSelectAddOns } from '@/features/booking-selection/views/SelectAddOns.hooks';
import {
  canOfferEmployeeChange,
  getBookableEmployeeListItems,
  toEmployeeListItem,
} from '@/features/booking-selection/views/SelectEmployeeList.helpers';
import { getAddableCategories } from '@/features/booking-selection/views/SelectService.helpers';
import { useSelectService } from '@/features/booking-selection/views/SelectService.hooks';
import PickTime from '@/features/weekpicker/PickTime';
import { usePickTime } from '@/features/weekpicker/PickTime.hooks';
import { useChangeBookingTime } from '@/features/weekpicker/useChangeBookingTime';
import { exportIdSlug } from '@/helpers/placeHelper';
import { hasDealCampagin } from '@/helpers/placeHelperTS';
import { serviceIsExtraTimeAddOn } from '@/helpers/service';
import { getExtraTimeRequirementForUser } from '@/helpers/userHelper';
import { useAppDispatch, useAppSelector } from '@/hooks';
import { _t } from '@/locale';
import { sortServices } from '@/reducers/bookReducer';
import { BookState, EXTRA_TIME_REQUIREMENT } from '@/types/state/book';
import { Place, PlaceService } from '@/types/state/shared';
import { useEffect, useRef, useState } from 'react';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';

// Every dispatch on this page lands in the store as `sortServices(...)`, so the list the page
// carries between renders has to be put through the same ordering: it is what decides whether an
// add-on whose main service just went is still part of the selection. A list assembled by hand
// here would name services the store has already let go, in `ss`/`rs` and in the tracked ids.
const storeOrder = (services: PlaceService[]): PlaceService[] => sortServices(services);

type PickTimePageProps = {
  place: Place;
  services: PlaceService[];
  employee?: number | null;
  time?: { timestamp?: number };
  campaignStart?: number;
  campaignEnd?: number;
  lastWorkingDays?: { last?: number; bookAhead?: number };
  /** The last-minute flow — the sistaminuten instance, or a place running a last-minute discount. */
  isSistaminuten?: boolean;
  hideEmployees?: boolean;
};

/**
 * The redesigned calendar step: the `usePickTime` container mounted inside the booking-flow
 * shell, with the selection summary and the employee panel as the desktop side column and
 * the SelectionsBar as the mobile sticky footer. The panel is the desktop counterpart of the
 * bar's employee link: the side column is hidden below `lg`, where the bar is shown instead.
 *
 * Rendered by the legacy calendar page's render branch, which keeps owning bootstrap — so
 * this page reads the already-hydrated `state.book` and deliberately does NOT call
 * `useBookingFlowSelection`: legacy entries (search results, place page, the `/share`
 * redirect) carry no `?ss/sr/rs/et`, and re-deriving from the URL would pop every picked
 * service. A URL that does carry `ss` describes its whole selection — an in-flow entry, a
 * reload, or a link published outside the marketplace — and is reconciled into the store on
 * arrival instead: only the difference is dispatched, so an entry that already matches the
 * store is left exactly as the bootstrap made it.
 */
const PickTimePage = ({
  place,
  services,
  employee,
  time,
  campaignStart,
  campaignEnd,
  lastWorkingDays,
  isSistaminuten,
  hideEmployees,
}: PickTimePageProps) => {
  const { placeSlugId, serviceSlugId } = useParams<{ placeSlugId: string; serviceSlugId: string }>();
  const history = useHistory();
  const location = useLocation();
  const { placePageTo, attribution } = useBookingFlowChrome();
  const book = useAppSelector((state) => state.book) as BookState;
  const { user } = useAppSelector((state) => state.users);
  const dispatch = useAppDispatch();

  // Change-time moves an existing booking to another slot, so the selection it was booked with
  // is fixed: nothing on this page may add, remove or re-staff it, nor offer the waitlist.
  const isChangeTime = Boolean(book.changeTimeBookingId);
  const changeBookingTime = useChangeBookingTime(book.changeTimeBookingId);

  const pickedServices = book.services ?? [];
  const mainServices = pickedServices.filter((service) => !service?.about?.settings?.isAddOn);
  // The extra-time add-on is deliberately not among these: it is signalled by `et` and no
  // employee "performs" it, so counting it would empty both the `rs` patch and the panel's
  // employee list.
  const addOns = pickedServices.filter(
    (service) => service?.about?.settings?.isAddOn && !serviceIsExtraTimeAddOn(service),
  );
  // Counts every add-on, extra-time included, which `addOns` deliberately does not — the legacy
  // pick-time page closes the add-services link on any picked add-on, and an extra-time answer
  // given for one main service says nothing about a second one picked after it.
  const hasPickedAddOn = pickedServices.some((service) => service?.about?.settings?.isAddOn);

  // Scoped to the same ids /valj-utovare derives from `ss` + `rs`, so the panel and the step
  // page can never disagree about who is bookable.
  const bookableServiceIds = [...mainServices, ...addOns].map((service) => service.id);
  const { bookableEmployees, items: employeeItems } = getBookableEmployeeListItems(place, bookableServiceIds);

  // One predicate for every employee-change affordance on this page: the bar's employee link,
  // the no-times recovery link, the mobile sheet and the desktop panel. `employeeItems`, not
  // hasMultipleEmployees: that counts bookable employees place-wide and reads "several" where
  // only one of them performs the picked services. The id list is checked separately because an
  // empty one makes every employee "perform" all of it.
  const canChangeEmployee =
    !isChangeTime &&
    !hideEmployees &&
    bookableServiceIds.length > 0 &&
    canOfferEmployeeChange(employeeItems, book.employee);

  // Deliberately local, not in the URL: both sheets sit *over* the calendar rather than being
  // steps, so neither must be deep-linkable or restorable — as with the desktop modal.
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [isEmployeeSheetOpen, setIsEmployeeSheetOpen] = useState(false);

  // The sheet at every width: /valj-utovare exists only below `lg`, where the desktop side
  // column's employee panel does not, so there is no step for this link to send anyone to.
  const onChangeEmployee = () => setIsEmployeeSheetOpen(true);

  // REMOVE_EMPLOYEE deletes `book.employee` instead of zeroing it, so an absent value is what
  // picking "Valfri utövare" leaves behind — on the calendar that is the any-employee filter,
  // not an unanswered step.
  const selectedEmployeeId = book.employee ?? ANY_EMPLOYEE_ID;

  // Picking writes both copies of the selection: redux, which `usePickTime` refetches
  // availability off (the `employee` prop is that same value, routed through the legacy page's
  // connect), and `sr`, which is what survives a reload or a return from checkout — the legacy
  // bootstrap clears redux and rebuilds the employee from the URL. Left naming the arrival
  // employee, `sr` would also let that bootstrap pull the pick back. `location.state` rides
  // through the replace: the header's back button reads `fromLanding` from it, checkout
  // `cancelBooking`. Legacy's own `removeHour` is not needed — this page holds no picked hour
  // (its first fetch runs `changeTime: true`).
  const setSelectedResource = (sr: string | null) =>
    history.replace({
      search: serialiseSelection(history.location.search, { sr }),
      state: history.location.state,
    });

  // The services' counterpart of `setSelectedResource`: every add and remove rewrites this
  // entry's `ss`/`rs` so a reload, a return from checkout or a link copied from the address
  // bar reproduces the selection. Replace, never push — cross-screen pops are measured from
  // this screen, and same-step entries here would change what back means mid-flow. The path
  // segment keeps naming its service for as long as that one is selected and moves to the first
  // remaining main only once it is dropped, so the URL never names a service the selection has
  // let go and a published link keeps its path whatever order the store lists the mains in —
  // the server seeds them in catalogue order, an edit in ascending id. Matched on the id, not
  // the whole slug-id, since a renamed service still resolves from a stale slug.
  // `selectionPatchFromBookState` keeps materialised extra-time add-ons out of `rs` — those are
  // `et`'s to signal.
  // Reads `history.location`, not the render-scoped one, as every writer in this feature does
  // (see appendPickedService): the drawer stays open across picks, so a second edit can be handled
  // before React has re-rendered, and serialising onto the stale entry would drop the params the
  // first one wrote.
  const replaceSelectionEntry = (nextServices: PlaceService[], patch: BookingFlowSelectionPatch) => {
    const current = history.location;
    const nextMains = nextServices.filter((service) => !service.about?.settings?.isAddOn);
    const pathMain = nextMains.find((service) => String(service.id) === exportIdSlug(serviceSlugId).id);
    const firstMain = nextMains[0];
    const pathname =
      !pathMain && firstMain
        ? ROUTE_BUILDER.bookingCalendar(placeSlugId, toSlugId(firstMain.about?.slug, firstMain.id))
        : current.pathname;
    const search = serialiseSelection(current.search, patch);
    if (pathname === current.pathname && search === current.search) return;
    history.replace({ pathname, search, state: current.state });
  };

  const servicesPatch = (nextServices: PlaceService[]): BookingFlowSelectionPatch => {
    const { ss, rs } = selectionPatchFromBookState({ ...book, services: nextServices }, place);
    return { ss, rs };
  };

  const onPickEmployee = (employeeId: number) => {
    if (employeeId === ANY_EMPLOYEE_ID) {
      dispatch(bookActions.removeEmployee());
      // The sentinel, not an absent `sr`: absent reads as "no employee step answered yet",
      // which is what makes /valj-utovare's guard render instead of bouncing.
      setSelectedResource(String(ANY_EMPLOYEE_ID));
      return;
    }
    const picked = bookableEmployees.find((candidate) => candidate.id === employeeId);
    if (!picked) return;
    dispatch(bookActions.pickEmployee(picked.id, picked.about?.priceListId || 0));
    // `sr` carries externalId, so an employee without one cannot be named in the URL; dropping
    // it loses only this employee's reload durability, where keeping the stale value would
    // revert the pick outright.
    setSelectedResource(picked.about?.externalId ?? null);
  };

  // The place page is where the marketplace picks another service; the widget hosts none, so
  // its chrome names the stand-in. The selection params are cleared either way: the step
  // pages re-derive the selection from the URL, and an `ss` left behind would re-pick the
  // very service that had no times.
  const onChooseAnotherService = () => {
    history.replace({
      pathname: placePageTo ? placePageTo(placeSlugId) : ROUTE_BUILDER.placePage(placeSlugId),
      search: serialiseSelection(location.search, { ss: null, sr: null, rs: null, et: null }),
    });
  };

  const dealId = hasDealCampagin(book.campaigns ?? [])?.id;
  // The deal `usePickTime` scopes its availability fetch by, which `book.campaigns` is not:
  // `setCampaigns` keeps a deal there only while it prices a picked service on the booked day,
  // whereas the server drops every slot outside the deal's window and employees for any deal id
  // it is handed. Asking the narrower list would offer the waitlist for an emptiness the deal
  // filter itself produced.
  const fetchDealId = hasDealCampagin(place.campaigns ?? [])?.id;

  // FR-019, as legacy gated it: the place has to support the waitlist, and neither a
  // deal, the last-minute flow nor change-time offers it.
  const canOfferWaitlist =
    Boolean(place.about?.settings?.supportsWaitlist) && !isSistaminuten && !fetchDealId && !isChangeTime;

  // Deliberately local, not in the URL: the modal sits *over* the calendar rather than
  // being a step, as with the selection modal and the drawer.
  const [isWaitlistOpen, setIsWaitlistOpen] = useState(false);

  const pickTimeProps = usePickTime({
    place,
    services,
    employee,
    time,
    campaignStart,
    campaignEnd,
    lastWorkingDays,
    isSistaminuten,
    onChooseAnotherService: isChangeTime ? undefined : onChooseAnotherService,
    onChangeEmployee: canChangeEmployee ? onChangeEmployee : undefined,
    onOpenWaitlist: canOfferWaitlist ? () => setIsWaitlistOpen(true) : undefined,
    onSelectChangeTimeSlot: isChangeTime ? changeBookingTime.onSelectSlot : undefined,
    refetchKey: changeBookingTime.failedMoveCount,
  });

  // Returns to whatever the visitor came from — the previous flow step for an in-flow entry,
  // the referring page otherwise.
  const barProps = useSelectionsBar(book.employee ? 'PickTime' : 'PickTimeAnyStaffMember', {
    onChangeEmployee: canChangeEmployee ? onChangeEmployee : undefined,
    onEdit: () => setIsDrawerOpen(true),
  });

  // Checkout sends a failed payment back here with `retry` (BookingCheckout.hooks), and the
  // slot it failed on is gone from the availability it re-fetches — without this the visitor
  // is returned to the calendar with no account of why.
  const retryState = location.state as { retry?: boolean; retryKlarna?: boolean; retryQliro?: boolean } | undefined;
  const retryMessage = !retryState?.retry
    ? undefined
    : retryState.retryKlarna
    ? _t('retryBookingKlarna')
    : retryState.retryQliro
    ? _t('retryBookingQliro')
    : _t('retryBooking');

  const { items, total, pickedEmployee } = buildBookSelectionSummary(book, place);

  // The card the drawer shows above the summary. ANY_EMPLOYEE_ID is a real answer here (it is
  // the any-employee filter, not an unanswered step), so it gets the same card treatment as a
  // person. The `employeeItems` lookup can miss when a deep link picked someone the
  // service-filtered list excludes, hence the place-record fallback.
  const employeeCard: SelectionDrawerEmployee | undefined = hideEmployees
    ? undefined
    : selectedEmployeeId === ANY_EMPLOYEE_ID
    ? {
        name: getAnyEmployee().about.name,
        jobTitle: getAnyEmployee().about.jobTitle,
        avatarIcon: 'users-alt',
      }
    : employeeItems.find((item) => item.id === selectedEmployeeId) ??
      (pickedEmployee ? toEmployeeListItem(pickedEmployee, place) : undefined);

  // Deliberately local, not in the URL: the modal sits *over* the calendar rather than being
  // a step, so it must not be deep-linkable or restorable.
  const [modalView, setModalView] = useState<SelectionModalView | null>(null);

  // The add_service funnel legacy reports from the same two entry points: opening either view
  // is an attempt, and dismissing it without picking is the abandonment leg. A pick closes the
  // modal on its own path, so `cancel` can never follow a `success`. The drawer reports the
  // same two legs through its own callbacks below.
  const openModal = (view: SelectionModalView) => {
    trackAddService('initiated');
    setModalView(view);
  };

  const dismissModal = () => {
    trackAddService('cancel');
    setModalView(null);
  };

  // `fetchTimes: false` is load-bearing: usePickTime already refetches availability whenever
  // the picked service ids change, so letting the action fetch too would issue two requests
  // per pick. The picked slot is passed as the date because the action re-derives
  // `book.campaigns` off it, and a campaign applies on the day being booked, not today.
  // Takes and returns the service list explicitly rather than reading `pickedServices`, so a
  // caller adding several in one tick (arrival from a link) hands each append the list the
  // previous one produced — the store is one render behind until the effect ends.
  const appendService = (service: PlaceService, current: PlaceService[]): PlaceService[] => {
    dispatch(
      // @ts-ignore appendService is a thunk; AppDispatch isn't typed thunk-aware (see BookAppointment.handlers.ts)
      bookActions.appendService(service, place, time?.timestamp, current, book.employee ?? 0, false, dealId),
    );
    // The extra-time answer is the visitor's, per place, not per service: a main service picked
    // after the previous owner (and its orphaned extra time) was removed gets the extra time it
    // lists, as the step pages' hydration does. Without this `et=1` and the requirement would
    // still say yes while the booking silently lost it.
    const servicesAfterAppend = [...current, service];
    const extraTimeRequirement = book.extraTimeRequirement || getExtraTimeRequirementForUser(user, place.id);
    const extraTimeAddOns =
      extraTimeRequirement === EXTRA_TIME_REQUIREMENT.NeedsExtraTime
        ? getNewExtraTimeAddOns(servicesAfterAppend, pickedEmployee ?? getAnyEmployee())
        : [];
    extraTimeAddOns.forEach((addOn) => {
      dispatch(bookActions.appendExtraService(addOn, servicesAfterAppend));
    });
    return storeOrder([...servicesAfterAppend, ...extraTimeAddOns]);
  };

  const appendPickedService = (service?: PlaceService) => {
    if (!service) return;
    const next = appendService(service, pickedServices);
    // `service_ids` is a session property the later choose_time and checkout events carry, so
    // it has to be refreshed here — the store update lands too late for this render. From the
    // list the append produced, not the pick alone: the extra time it materialises is booked too.
    bookingFlowTrackingSession.setProperties({
      service_ids: next.map((picked) => String(picked.id)),
    });
    bookingFlowTrackingSession.addService('success');
    replaceSelectionEntry(next, servicesPatch(next));
  };

  const onAddMainService = (serviceId: number) =>
    appendPickedService(
      (place.services ?? []).flatMap((category) => category.services ?? []).find((service) => service.id === serviceId),
    );

  // Resolved from the very list useSelectAddOns derives its rows from, not from the place
  // catalog: recommended add-ons come off the main service's `extra.addOnServices` and are
  // not guaranteed to appear among `place.services`.
  const onAddRecommendedService = (serviceId: number) =>
    appendPickedService(
      getRecommendedServices(pickedServices, pickedEmployee ?? getAnyEmployee()).find(
        (service) => service.id === serviceId,
      ),
    );

  const addableServiceIds = new Set(
    getAddableCategories(place, pickedServices, pickedEmployee).flatMap((category) =>
      (category.services ?? []).map((service) => service.id),
    ),
  );

  const recommendedProps = useSelectAddOns({ onPickService: onAddRecommendedService });
  // Narrowed inside the hook rather than over its result, so the accordion's only-category
  // and first-category rules are decided on the list the modal actually shows.
  const addServiceProps = useSelectService({
    onPickService: onAddMainService,
    includeService: (serviceId) => addableServiceIds.has(serviceId),
  });

  // The gate is the same derivation the modal renders, so a link can never open an empty
  // modal. The add-on and deal conjuncts mirror the legacy page's add-more-services gate.
  const canAddServices = !dealId && !hasPickedAddOn && addableServiceIds.size > 0;
  const canAddRecommended = recommendedProps.services.length > 0;

  // popService, never removeService: REMOVE_SERVICE's reducer case returns an empty booking.
  // `skipAvailabilityCall` is the counterpart of `fetchTimes: false` above, and the picked
  // slot is the date the campaigns are re-derived against, as in appendService. Each pop
  // re-derives book.campaigns from the list it is handed, so the list has to shrink with them
  // rather than every call being given the pre-removal selection.
  const popServices = (ids: number[], current: PlaceService[]): PlaceService[] =>
    ids.reduce((remaining, id) => {
      dispatch(
        // @ts-ignore popService is a thunk; AppDispatch isn't typed thunk-aware (see BookAppointment.handlers.ts)
        bookActions.popService(id, time?.timestamp, remaining, book.employee ?? 0, place, true),
      );
      return storeOrder(remaining.filter((service) => service.id !== id));
    }, current);

  // An extra-time add-on whose main service just went has no row to be removed from and no
  // owner to be priced onto, but would still be charged and booked, so it goes with it.
  const withOrphanedExtraTime = (ids: number[], current: PlaceService[]): number[] => {
    const remaining = current.filter((service) => !ids.includes(service.id));
    return [...ids, ...getOrphanedExtraTimeAddOnIds(remaining)];
  };

  const onRemoveService = (serviceId: number) => {
    const removed = pickedServices.find((service) => service.id === serviceId);
    if (!removed) return;
    // The same rule the summary's remove affordance and `removePickedService` apply: a booking
    // with no main service is not a state the flow can produce, and `ss` is set-only, so an
    // emptied selection would leave the URL naming the service just removed — which the next
    // arrival would faithfully put back.
    if (!removed.about?.settings?.isAddOn && mainServices.length < 2) return;
    const removedIds = withOrphanedExtraTime([serviceId], pickedServices);
    const next = popServices(removedIds, pickedServices);
    bookingFlowTrackingSession.setProperties({
      service_ids: next.map((picked) => String(picked.id)),
    });
    bookingFlowTrackingSession.removedService();
    replaceSelectionEntry(next, servicesPatch(next));
  };

  // Arrival from a URL that names its selection. Mount-scoped on purpose: this page is the only
  // writer of its own entry and every write follows a store change, so re-running on
  // `location.search` would only redo work; the ref also holds through StrictMode's second
  // effect pass, which would otherwise announce a rotted link twice. The store is reconciled to
  // the URL — pops for what it no longer names, appends for what it adds, the employee re-judged
  // against the whole selection — through the same paths the visitor's own edits take, minus
  // their tracking. An in-flow entry already matches the store, so it dispatches nothing. The
  // entry is then normalised once so that dropped ids leave the address bar and a reload does
  // not announce them again; `sr` is written only when the URL carried one, since an absent `sr`
  // means the store's employee stands.
  const hydratedForPlaceRef = useRef<number | undefined>(undefined);
  useEffect(() => {
    if (hydratedForPlaceRef.current === place.id) return;
    hydratedForPlaceRef.current = place.id;

    const resolved = resolveCalendarSelectionFromUrl(location.search, place, mainServices);
    if (!resolved) return;

    const target = [...resolved.mainServices, ...resolved.addOns];
    const toPop = pickedServices.filter(
      (service) => !serviceIsExtraTimeAddOn(service) && !target.some((wanted) => wanted.id === service.id),
    );
    const toAppend = target.filter((wanted) => !pickedServices.some((service) => service.id === wanted.id));

    let next = pickedServices;
    if (toPop.length > 0) {
      next = popServices(
        withOrphanedExtraTime(
          toPop.map((service) => service.id),
          next,
        ),
        next,
      );
    }
    toAppend.forEach((service) => {
      next = appendService(service, next);
    });

    let nextEmployee = book.employee;
    if (resolved.employee.kind === 'pick' && book.employee !== resolved.employee.employee.id) {
      const picked = resolved.employee.employee;
      dispatch(bookActions.pickEmployee(picked.id, picked.about?.priceListId || 0));
      dispatch(bookActions.keepEmployee());
      nextEmployee = picked.id;
    } else if (resolved.employee.kind === 'any' && book.employee != null && book.employee !== ANY_EMPLOYEE_ID) {
      dispatch(bookActions.removeEmployee());
      nextEmployee = undefined;
    }

    if (next !== pickedServices) syncTrackedServiceIds(next);

    // The sentinel stands in for "any" so that a dropped employee leaves `sr=0` behind, not the
    // externalId of someone the calendar is no longer scoped to.
    const patch = selectionPatchFromBookState(
      { ...book, services: next, employee: resolved.employee.kind === 'any' ? ANY_EMPLOYEE_ID : nextEmployee },
      place,
    );
    replaceSelectionEntry(next, resolved.employee.kind === 'keep' ? { ss: patch.ss, rs: patch.rs } : patch);

    const notices: string[] = [];
    const droppedCount = resolved.dropped.serviceIds.length;
    if (droppedCount === 1) {
      notices.push(_t('features.bookingFlow.calendarLink.servicesDropped_one', { count: droppedCount }));
    } else if (droppedCount > 1) {
      notices.push(_t('features.bookingFlow.calendarLink.servicesDropped_other', { count: droppedCount }));
    }
    if (resolved.dropped.employee === 'missing') {
      notices.push(_t('features.bookingFlow.calendarLink.employeeMissing'));
    } else if (resolved.dropped.employee === 'cannotPerform') {
      notices.push(_t('features.bookingFlow.calendarLink.employeeCannotPerform'));
    }
    if (notices.length > 0) {
      toast(({ closeToast }) => <Snackbar label={notices.join(' ')} type="info" onClose={closeToast} />);
    }
    // Deliberately not depending on the store or the URL: this must run once per place, never
    // because its own dispatches or its own replace changed them.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [place.id]);

  // Change-time renders the calendar alone: the side column and the bar both exist to review
  // and edit a selection this flow has fixed.
  const sideColumn = isChangeTime ? undefined : (
    <div className="gap-xl flex flex-col">
      <SelectionSummary
        items={items}
        total={total}
        onRemove={onRemoveService}
        onAddServices={canAddServices ? () => openModal('addService') : undefined}
        onAddRecommended={canAddRecommended ? () => openModal('recommended') : undefined}
      />
      {canChangeEmployee ? (
        <SelectedEmployeePanel
          heading={_t('features.bookingSelection.SelectedEmployeePanel.heading')}
          employees={employeeItems}
          selectedEmployeeId={selectedEmployeeId}
          onPick={onPickEmployee}
        />
      ) : null}
      {attribution?.('column')}
    </div>
  );

  return (
    <BookingFlowPage
      title={isChangeTime ? _t('changeTime') : _t('pickTime')}
      subtitle={place.about?.name}
      abortPath={isChangeTime ? `/bokning/${book.changeTimeBookingId}` : undefined}
      merchantLogoUrl={place.about?.profileImage}
      sideColumn={sideColumn}
      columnGap="narrow"
      contentInset="bleed"
      stickyMainColumn
      trackServiceDetails={false}
      bottomBar={isChangeTime ? undefined : <SelectionsBar {...barProps} />}>
      {retryMessage ? (
        // The bleed inset leaves the narrow content box no gutters for this to sit in, and the
        // calendar's own edge-to-edge surface is not a shape a message should take.
        <div className="mx-cv-padding mt-cv-margin mb-lg lg:mx-0 lg:mt-0">
          <StatusMessage type="error" message={retryMessage} />
        </div>
      ) : null}
      {/* The shrink seam the pinned column needs: the retry message keeps its own height and
          the calendar absorbs the rest, so its slot list scrolls rather than the page. */}
      <div className="min-h-0 flex-1">
        <PickTime {...pickTimeProps} />
      </div>
      {/* In the column's flow, not the sticky bar: it stays under the calendar's last row.
          From `lg` the side column carries it instead. */}
      {attribution ? <div className="shrink-0 lg:hidden">{attribution('bar')}</div> : null}
      {/* Desktop-only by reachability, not CSS: the side column that opens it is `lg:block`,
          so nothing below 1024px can reach either link. */}
      {isChangeTime ? null : (
        <SelectionModal
          view={modalView}
          onClose={dismissModal}
          // Closing on a pick is the modal's own behaviour, not the appender's: the drawer keeps
          // its view stack and pops back to the summary instead.
          recommended={{
            ...recommendedProps,
            onPickService: (serviceId, isRecommended) => {
              recommendedProps.onPickService(serviceId, isRecommended);
              setModalView(null);
            },
          }}
          addService={{
            ...addServiceProps,
            onPickService: (serviceId, isRecommended) => {
              addServiceProps.onPickService(serviceId, isRecommended);
              setModalView(null);
            },
          }}
          employeeInfo={
            canChangeEmployee
              ? {
                  heading: _t('features.bookingSelection.SelectService.employeePanelHeading'),
                  employees: employeeItems,
                  selectedEmployeeId,
                }
              : undefined
          }
        />
      )}
      {changeBookingTime.pendingTimestamp !== undefined && (
        <ConfirmChangeBookingTimeModal
          isOpen
          loading={changeBookingTime.isSubmitting}
          newTimestamp={changeBookingTime.pendingTimestamp}
          onClose={changeBookingTime.onClose}
          onConfirm={changeBookingTime.onConfirm}
        />
      )}
      {isWaitlistOpen && (
        <WaitlistModal
          isOpen
          onRequestClose={() => setIsWaitlistOpen(false)}
          // A place that hides its providers must not have one pinned in the link: a lone
          // bookable employee is picked for the visitor, and `sr` can seed one, so an
          // employee the visitor was never shown would otherwise scope the signup.
          deepLink={SMART_LINKS.getShareSmartLink(
            place.id,
            pickedServices.map((service) => service.id),
            hideEmployees ? undefined : book.employee || undefined,
          )}
        />
      )}
      {/* Mobile-only by reachability, not CSS: BottomSheetModal portals to document.body, so
          only the SelectionsBar (itself `lg:hidden`) can open either of these. */}
      {isChangeTime ? null : (
        <>
          <SelectionDrawer
            isOpen={isDrawerOpen}
            onClose={() => setIsDrawerOpen(false)}
            employee={employeeCard}
            // No `onSearchTimes`: the visitor is already on the calendar, so the summary renders
            // without the CTA and the ✕ is the only exit.
            summary={{ items, total, onRemove: onRemoveService }}
            recommended={recommendedProps}
            addService={canAddServices ? addServiceProps : undefined}
            {...addServiceTrackingHandlers}
            employeeList={{ employees: employeeItems, selectedEmployeeId, onPick: onPickEmployee }}
          />
          <SelectEmployeeSheet
            isOpen={isEmployeeSheetOpen}
            onClose={() => setIsEmployeeSheetOpen(false)}
            employeeList={{ employees: employeeItems, selectedEmployeeId, onPick: onPickEmployee }}
          />
        </>
      )}
    </BookingFlowPage>
  );
};

export default PickTimePage;
